/* eslint-disable @typescript-eslint/no-explicit-any */
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { FaxServices } from '../services/services';
import type { Fax } from '../types/types';

type Status = "all" | "pending" | "sent" | "delivered" | "failed" | "received" | "cancelled";
type Direction = "all" | "inbound" | "outbound";

export const useInfiniteFaxHistory = (opts?: { search?: string; status?: Status; direction?: Direction; limit?: number }) => {
  const limit = opts?.limit ?? 20;
  const serverStatus = opts?.status && opts.status !== "all" ? opts.status : undefined;
  const serverDirection = opts?.direction && opts.direction !== "all" ? opts.direction : undefined;

  const q = useInfiniteQuery({
    queryKey: ["fax-history", serverStatus, serverDirection, limit],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      FaxServices.getFaxHistory({
        limit,
        offset: Number(pageParam) || 0,
        status: serverStatus as any,
        direction: serverDirection as any,
      }),
    getNextPageParam: (lastPage, pages) => {
      // Response is: { items: [...], meta: { page, totalItems, totalPages, itemCount } }
      const items = (lastPage as any)?.items ?? [];
      const meta = (lastPage as any)?.meta;
      // If we have meta info, use it for pagination
      if (meta?.totalPages && meta.page < meta.totalPages) {
        return pages.length * limit;
      }
      // Fallback: if items count is less than limit, we're at the end
      return items.length < limit ? undefined : pages.length * limit;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true, // Ensure fresh data on mount
    retry: (failureCount, error) => {
      // Don't retry if we've failed too many times
      if (failureCount >= 2) return false;

      const errorMessage = error?.message || '';

      // Don't retry on client errors (4xx) including 404, 401, 403
      if (errorMessage.includes('401') ||
          errorMessage.includes('403') ||
          errorMessage.includes('404') ||
          errorMessage.includes('400')) {
        return false;
      }

      // Only retry on server errors (5xx) or network issues
      if (errorMessage.includes('500') ||
          errorMessage.includes('502') ||
          errorMessage.includes('503') ||
          errorMessage.includes('Network') ||
          errorMessage.includes('timeout')) {
        return true;
      }

      // Default: don't retry unknown errors to prevent infinite loops
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 5000), // Linear backoff with max 5s
    // Return fallback data on error to prevent crashes - following user memory preferences
    throwOnError: false,
  });

  const faxes: Fax[] = useMemo(() => {
    const pages = q.data?.pages ?? [];
    // Response structure is: { items: Fax[], meta: {...} }
    const all = pages.flatMap((p: any) => {
      const items = p?.items ?? [];
      return Array.isArray(items) ? items : [];
    }) as Fax[];
    const needle = (opts?.search ?? "").trim().toLowerCase();
    if (!needle) return all;
    return all.filter((f) => {
      const docs = f.documents?.map((d) => d.path?.toLowerCase()) ?? [];
      return (
        f.to?.toLowerCase().includes(needle) ||
        f.from?.toLowerCase().includes(needle) ||
        f.coverPageName?.toLowerCase().includes(needle) ||
        f.faxId?.toLowerCase().includes(needle) ||
        docs.some((p) => p?.includes(needle)) ||
        (f.failureReason ? f.failureReason.toLowerCase().includes(needle) : false)
      );
    });
  }, [q.data, opts?.search]);

  return {
    faxes,
    fetchNextPage: q.fetchNextPage,
    hasNextPage: q.hasNextPage,
    isFetchingNextPage: q.isFetchingNextPage,
    isLoading: q.isLoading,
    error: q.error,
  };
};